<template>
  <div class="modal-overlay">
    <div class="modal-content-wrapper">
      <div class="modal-header">
        {{ titleText }}
        <CloseSvg
          class="close"
          :dark="true"
          @click="onCloseClicked"
        />
      </div>
      <div class="modal-content">
        <div class="edit-contact-flex-container-row">
          <TextInput
            v-model="firstName"
            :show-required="true"
            label="First Name"
            class="first-item"
          />
          <TextInput
            v-model="lastName"
            :show-required="true"
            label="Last Name"
            class="second-item"
          />
        </div>
        <div class="edit-contact-flex-container-row">
          <TextInput
            v-model="email"
            label="Email"
            class="first-item"
          />
          <PhoneNumberInput
            v-model="phoneNumber"
            class="second-item"
          />
        </div>
      </div>
      <div class="modal-footer">
        <LockingButton
          class="save-button"
          :disabled="!isFormValid"
          :locked="saving"
          :text="'Save'"
          @click="onSaveClicked"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { Contact } from '@/@types/responses/contact/Contact'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { ContactMessage } from '@/@types/messages/ContactMessage'
import { ManufacturerService } from '@/services/manufacturer.service'
import { SupplierService } from '@/services/supplier.service'
import { ContactService } from '@/services/contact.service'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'ContactModal',
  components: {
    CloseSvg,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  props: {
    contact: {
      type: Contact,
      required: true
    },
    manufacturerId: {
      type: Number,
      default: undefined
    },
    supplierId: {
      type: Number,
      default: undefined
    }
  },
  emits: ['contact-changed', 'close'],
  setup() {
    const contactService = inject(PROVIDER_CONSTANTS.contactService) as ContactService
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      contactService,
      manufacturerService,
      supplierService,
      toast
    }
  },
  data() {
    return {
      firstName: this.contact.firstName,
      lastName: this.contact.lastName,
      phoneNumber: this.contact.phoneNumber,
      email: this.contact.email,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.firstName !== this.contact.firstName ||
             this.lastName !== this.contact.lastName ||
             this.phoneNumber !== this.contact.phoneNumber ||
             this.email !== this.contact.email) && (this.firstName.trim() !== '' && this.lastName.trim() !== '')
    },
    titleText(): string {
      return this.contact.id === 0 ? 'Create Contact' : 'Edit Contact'
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const contactMessage = new ContactMessage(this.firstName, this.lastName, this.phoneNumber, this.email)
      if (this.contact.id === 0) {
        if (this.manufacturerId !== undefined)
          this.manufacturerService.createManufacturerContact(this.manufacturerId, contactMessage).then(() => {
            this.$emit('contact-changed')
            this.toast.success('Contact created successfully.')
            this.$emit('close')
          }).catch(error => {
            this.saving = false

            if (error instanceof ErrorsResponse) {
              const errorsResponse = error as ErrorsResponse

              if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

              this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
            } else {
              this.toast.error('Failed to create the contact. Please try again.')
            }
          })
        else if (this.supplierId !== undefined)
          this.supplierService.createSupplierContact(this.supplierId, contactMessage).then(() => {
            this.$emit('contact-changed')
            this.toast.success('Contact created successfully.')
            this.$emit('close')
          }).catch(error => {
            this.saving = false

            if (error instanceof ErrorsResponse) {
              const errorsResponse = error as ErrorsResponse

              if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

              this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
            } else {
              this.toast.error('Failed to create the contact. Please try again.')
            }
          })
      } else
        this.contactService.updateContact(this.contact.id, contactMessage).then(() => {
          this.$emit('contact-changed')
          this.toast.success('Contact updated successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to updated the contact. Please try again.')
          }
        })
    }
  }
})
</script>

<style scoped lang="scss">
.modal-overlay {
  @include modal-styles;
}

.modal-overlay > .modal-content-wrapper {
  > .modal-content > .edit-contact-flex-container-row {
    display: flex;
    flex-direction: column;

    @include desktop {
      flex-direction: row;
      margin-bottom: 15px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    > .first-item {
      margin-bottom: 15px;
      flex: 1;

      @include desktop {
        flex: 0 0 calc(50% - 10px);
        margin-right: 10px;
        margin-bottom: 0;
      }
    }

    > .second-item {
      margin-bottom: 15px;
      flex: 1;

      @include desktop {
        flex: 0 0 50%;
        margin-bottom: 0;
      }
    }
  }
}
</style>